import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { UserX } from "lucide-react";
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/ajax/clients/supabase";
import { queryClient } from "@/ajax/queryClient";
import { buildProviderInviteRedirect } from "../utils/invite-redirect";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function InvitationPendingMismatchCard({
  token,
  invitedEmail,
  signedInEmail,
}: {
  token: string;
  invitedEmail: string;
  signedInEmail: string;
}) {
  const navigate = useNavigate();
  const [isSwitching, setIsSwitching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const switchAccount = async () => {
    setIsSwitching(true);
    setErrorMessage(undefined);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      queryClient.clear();
      navigate({
        to: "/auth-provider/signin",
        search: { redirect: buildProviderInviteRedirect(token) },
      });
    } catch {
      setErrorMessage("Could not sign out. Please try again.");
      setIsSwitching(false);
    }
  };

  return (
    <>
      <CardHeader className="items-center text-center">
        <div className="rounded-full bg-amber-100 p-3 mb-2">
          <UserX className="h-6 w-6 text-amber-600" />
        </div>
        <CardTitle className="text-xl">Different email signed in</CardTitle>
        <CardDescription>
          This invitation was sent to <strong>{invitedEmail}</strong>, but
          you&apos;re signed in as <strong>{signedInEmail}</strong>. If this
          invitation should be yours, ask the organization to resend it to your
          email address.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
        <Button
          variant="outline"
          className="w-full"
          onClick={switchAccount}
          disabled={isSwitching}
        >
          {isSwitching ? "Signing out…" : "Switch account"}
        </Button>
      </CardContent>
    </>
  );
}
